// ============================================================================
// GAME LOOP - Fixed Timestep with Interpolation
// ============================================================================

export type UpdateCallback = (dt: number, time: number) => void;
export type RenderCallback = (alpha: number) => void;

export class GameLoop {
  private running = false;
  private lastTime = 0;
  private accumulator = 0;
  private frameId = 0;

  private readonly fixedDeltaTime: number;
  private readonly maxFrameTime: number;

  private onUpdate: UpdateCallback;
  private onRender: RenderCallback;

  // Performance metrics
  private fps = 0;
  private frameCount = 0;
  private fpsTimer = 0;
  private tickCount = 0;
  private tps = 0;

  constructor(
    tickRate: number,
    onUpdate: UpdateCallback,
    onRender: RenderCallback
  ) {
    this.fixedDeltaTime = 1000 / tickRate;
    this.maxFrameTime = this.fixedDeltaTime * 5; // Prevent spiral of death
    this.onUpdate = onUpdate;
    this.onRender = onRender;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    this.frameCount = 0;
    this.tickCount = 0;
    this.fpsTimer = this.lastTime;
    this.loop();
  }

  stop(): void {
    this.running = false;
    if (this.frameId) {
      cancelAnimationFrame(this.frameId);
      this.frameId = 0;
    }
  }

  private loop = (): void => {
    if (!this.running) return;

    const currentTime = performance.now();
    let frameTime = currentTime - this.lastTime;
    this.lastTime = currentTime;

    // Clamp frame time to prevent spiral of death
    if (frameTime > this.maxFrameTime) {
      frameTime = this.maxFrameTime;
    }

    this.accumulator += frameTime;

    // Fixed timestep updates
    while (this.accumulator >= this.fixedDeltaTime) {
      this.onUpdate(this.fixedDeltaTime, currentTime);
      this.accumulator -= this.fixedDeltaTime;
      this.tickCount++;
    }

    // Render with interpolation alpha
    const alpha = this.accumulator / this.fixedDeltaTime;
    this.onRender(alpha);
    this.frameCount++;

    // Calculate FPS/TPS every second
    if (currentTime - this.fpsTimer >= 1000) {
      this.fps = this.frameCount;
      this.tps = this.tickCount;
      this.frameCount = 0;
      this.tickCount = 0;
      this.fpsTimer = currentTime;
    }

    this.frameId = requestAnimationFrame(this.loop);
  };

  getFPS(): number {
    return this.fps;
  }

  getTPS(): number {
    return this.tps;
  }

  isRunning(): boolean {
    return this.running;
  }

  getFixedDeltaTime(): number {
    return this.fixedDeltaTime;
  }
}
